import React, { CSSProperties, useState } from "react";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { TIER_DETAILS, TierDetail, TierPerk } from "../tierDetails";
import GroundTourModal from "./GroundTourModal";

interface Props {
  open: boolean;
  onClose: () => void;
  // The tour's own tier (its `brand`); the mobile switcher opens on it.
  tier?: string;
}

export default function GroundTourTierModal({ open, onClose, tier }: Props) {
  const isMobile = useBreakpoint();
  // Unmounting while closed means each opening starts fresh (mobile: on the tour's own tier).
  if (!open) return null;

  return (
    <GroundTourModal
      open={open}
      onClose={onClose}
      title="Tour Tier"
      isMobile={isMobile}
      width={825}
      fullScreen
      className={`ground-tour-show-v2__tier-popup ${isMobile ? "" : "ground-tour-show-v2__tier-popup--desktop"}`}
    >
      {isMobile ? <TierSwitcher initial={tier} /> : <TierPanels />}
    </GroundTourModal>
  );
}

// The crown is two exported pieces (band + crown) that Figma stacks, so they stay two images.
function TierCrown({ detail }: { detail: TierDetail }) {
  return (
    <span className="ground-tour-show-v2__tier-crown" aria-hidden="true">
      <span className="ground-tour-show-v2__tier-crown-base">
        <img src={detail.crownBase} alt="" />
      </span>
      <span className="ground-tour-show-v2__tier-crown-top">
        <img src={detail.crownTop} alt="" />
      </span>
    </span>
  );
}

function PerkIcon({ perk }: { perk: TierPerk }) {
  const style = perk.inset
    ? ({ "--inset-y": perk.inset[0], "--inset-x": perk.inset[1] } as CSSProperties)
    : undefined;

  return (
    <span className="ground-tour-show-v2__tier-perk-icon" style={style}>
      <img src={perk.icon} alt="" aria-hidden="true" />
    </span>
  );
}

function TierSwitcher({ initial }: { initial?: string }) {
  const [activeValue, setActiveValue] = useState(initial);
  const active = TIER_DETAILS.find((detail) => detail.value === activeValue) ?? TIER_DETAILS[0];

  return (
    <div className="ground-tour-show-v2__tier-details">
      <div className="ground-tour-show-v2__tier-tabs" role="tablist" aria-label="Tour tier">
        {TIER_DETAILS.map((detail) => (
          <button
            key={detail.value}
            type="button"
            role="tab"
            id={`ground-tour-tier-tab-${detail.value}`}
            aria-selected={detail.value === active.value}
            aria-controls="ground-tour-tier-panel"
            className={`ground-tour-show-v2__tier-tab ${detail.value === active.value ? "is-active" : ""}`}
            onClick={() => setActiveValue(detail.value)}
          >
            {detail.tab}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id="ground-tour-tier-panel"
        aria-labelledby={`ground-tour-tier-tab-${active.value}`}
        className={`ground-tour-show-v2__tier-card ground-tour-show-v2__tier-card--${active.value}`}
      >
        <div className="ground-tour-show-v2__tier-badge" aria-hidden="true">
          <img className="ground-tour-show-v2__tier-badge-shape" src={active.badge} alt="" />
          <TierCrown detail={active} />
        </div>

        <h3 className="ground-tour-show-v2__tier-heading">{active.heading}</h3>

        <ul className="ground-tour-show-v2__tier-perks">
          {active.perks.map((perk) => (
            <li key={perk.text} className="ground-tour-show-v2__tier-perk">
              <PerkIcon perk={perk} />
              <span>{perk.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function TierPanels() {
  return (
    <div className="ground-tour-show-v2__tier-scroll">
      <div className="ground-tour-show-v2__tier-panels">
        {TIER_DETAILS.map((detail) => (
          <section
            key={detail.value}
            aria-label={detail.heading}
            className={`ground-tour-show-v2__tier-panel ground-tour-show-v2__tier-panel--${detail.value}`}
          >
            <span className="ground-tour-show-v2__tier-panel-glow" aria-hidden="true">
              <span>
                <img src={detail.panelGlow} alt="" />
              </span>
            </span>
            <span className="ground-tour-show-v2__tier-panel-shape" aria-hidden="true">
              <img src={detail.panelShape} alt="" />
            </span>
            <TierCrown detail={detail} />

            <h3 className="ground-tour-show-v2__tier-panel-heading">{detail.heading}</h3>

            <ul className="ground-tour-show-v2__tier-panel-perks">
              {detail.perks.map((perk) => (
                <li key={perk.text} className="ground-tour-show-v2__tier-panel-perk">
                  <PerkIcon perk={perk} />
                  <span>{perk.text}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
